import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { MessageBox } from 'react-chat-elements';
import { FaRegCopy } from 'react-icons/fa';
import { useChatContext } from '../context/ChatContext';
import BotMessage from './BotMessage';

const FeedbackSection = lazy(() => import('./FeedbackSection'));

const MessageList = () => {
  const { messages, dispatch, isStreaming } = useChatContext();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const handleCopyMessage = async (messageIndex) => {
    try {
      const message = messages[messageIndex];
      if (!message) throw new Error('No message found');
      let textToCopy = message.text?.trim() || message.rawText?.trim() || '';
      if (!textToCopy) throw new Error('Message is empty');
      
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!ok) throw new Error('Copy command failed');
      }
      
      dispatch({
        type: 'UPDATE_MESSAGE_BY_ID',
        payload: { id: message.id, updates: { copyStatus: 'copied' } }
      });
      
      setTimeout(() => {
        dispatch({
          type: 'UPDATE_MESSAGE_BY_ID',
          payload: { id: message.id, updates: { copyStatus: null } }
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
      const message = messages[messageIndex];
      if (message) {
        dispatch({
          type: 'UPDATE_MESSAGE_BY_ID',
          payload: { id: message.id, updates: { copyStatus: 'error' } }
        });
        
        setTimeout(() => {
          dispatch({
            type: 'UPDATE_MESSAGE_BY_ID',
            payload: { id: message.id, updates: { copyStatus: null } }
          });
        }, 2000);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    shouldAutoScrollRef.current = isNearBottom;
  };

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      scrollToBottom();
    }
  }, [messages, isStreaming]);

  return (
    <div className="chat-messages" ref={containerRef} onScroll={handleScroll}>
      {messages.map((msg, index) => (
        <div key={msg.id} className="message-row">
          <div className="message-wrapper">
            {msg.isBot ? (
              <BotMessage message={msg} />
            ) : (
              <MessageBox position={msg.position} type={msg.type} text={msg.text} date={msg.date} />
            )}
            <div className={`copy-button-row ${msg.position === 'right' ? 'align-right' : 'align-left'}`}>
              <button 
                className={`copy-button ${msg.copyStatus ? msg.copyStatus : ''}`} 
                onClick={() => handleCopyMessage(index)} 
                title="Copy"
              >
                {msg.copyStatus === 'copied' ? '✓' : <FaRegCopy />}
              </button>
            </div>
            {msg.isBot && (
              <Suspense fallback={<div>Loading...</div>}>
                <FeedbackSection message={msg} />
              </Suspense>
            )}
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;